
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SavedOutfit, Lookbook, AppPage, SavedContent, UserProfile } from '../types';
import { useTranslation } from '../i18n/useTranslation';
import { BookOpenIcon, HeartIcon, Trash2Icon, FileTextIcon, PlusIcon, CheckCircleIcon, LayersIcon, PaletteIcon } from '../components/icons';
import StyleAnalyticsDashboard from '../components/StyleAnalyticsDashboard';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import ColorStudioPage from './ColorStudioPage';
import MagazineLookbook from '../components/MagazineLookbook';

interface MyStylesPageProps {
  savedOutfits: SavedOutfit[];
  lookbooks: Lookbook[];
  savedContent: SavedContent[];
  onDeleteContent: (id: string) => void;
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
  favoritedOutfitIds: Set<string>;
  onToggleFavorite: (outfitId: string) => void;
  onLoadOutfit: (outfit: SavedOutfit) => void;
  onDeleteOutfit: (id: string) => void;
  onShowLookbookCreator: () => void;
  userProfile: UserProfile;
}

export const MyStylesPage: React.FC<MyStylesPageProps> = ({ savedOutfits, lookbooks, savedContent, onDeleteContent, onNavigate, favoritedOutfitIds, onToggleFavorite, onLoadOutfit, onDeleteOutfit, onShowLookbookCreator, userProfile }) => {
  const { t } = useTranslation();

  useSEO(
    t('myStylesPage_seo_title'),
    t('myStylesPage_seo_desc'),
    t('myStylesPage_seo_keywords').split(',').map(k => k.trim())
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<'outfits' | 'favorites' | 'lookbooks' | 'advice' | 'analytics' | 'color_analysis'>(
    (tabParam as 'outfits' | 'favorites' | 'lookbooks' | 'advice' | 'analytics' | 'color_analysis') || 'outfits'
  );
  const [filterText, setFilterText] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'name-asc' | 'name-desc'>('newest');

  // Update active tab if query param changes
  React.useEffect(() => {
    if (tabParam && ['outfits', 'favorites', 'lookbooks', 'advice', 'analytics', 'color_analysis'].includes(tabParam)) {
      setActiveTab(tabParam as any);
    }
  }, [tabParam]);

  // Comparison state
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [comparisonIds, setComparisonIds] = useState<Set<string>>(new Set());

  // Magazine state
  const [selectedMagazine, setSelectedMagazine] = useState<Lookbook | null>(null);

  const outfitsToShow = useMemo(() => {
    return activeTab === 'favorites'
      ? savedOutfits.filter(o => favoritedOutfitIds.has(o.id))
      : savedOutfits;
  }, [activeTab, savedOutfits, favoritedOutfitIds]);

  const filteredAndSortedOutfits = useMemo(() => {
    const lowercasedFilter = filterText.toLowerCase();
    let currentOutfits = outfitsToShow.filter(outfit =>
      outfit.name.toLowerCase().includes(lowercasedFilter) ||
      outfit.description?.toLowerCase().includes(lowercasedFilter) ||
      outfit.tags?.some(tag => tag.toLowerCase().includes(lowercasedFilter))
    );

    switch (sortOrder) {
      case 'newest':
        currentOutfits.sort((a, b) => parseInt(b.id.split('-')[1] || '0') - parseInt(a.id.split('-')[1] || '0'));
        break;
      case 'oldest':
        currentOutfits.sort((a, b) => parseInt(a.id.split('-')[1] || '0') - parseInt(b.id.split('-')[1] || '0'));
        break;
      case 'name-asc':
        currentOutfits.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        currentOutfits.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }
    return currentOutfits;
  }, [outfitsToShow, filterText, sortOrder]);

  const filteredAndSortedLookbooks = useMemo(() => {
    let currentLookbooks = lookbooks.filter(lookbook =>
      lookbook.name.toLowerCase().includes(filterText.toLowerCase())
    );
    return currentLookbooks;
  }, [lookbooks, filterText]);

  const filteredAndSortedContent = useMemo(() => {
    let currentContent = savedContent.filter(item =>
      item.title.toLowerCase().includes(filterText.toLowerCase())
    );

    // Filter by type based on active tab
    if (activeTab === 'advice') {
      currentContent = currentContent.filter(item => item.type !== 'color_analysis' && item.type !== 'analysis');
    } else if (activeTab === 'color_analysis') {
      currentContent = currentContent.filter(item => item.type === 'color_analysis' || item.type === 'analysis');
    }

    currentContent.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return currentContent;
  }, [savedContent, filterText, activeTab]);

  const favoritedCount = favoritedOutfitIds.size;

  const handleToggleCompareMode = () => {
    setIsCompareMode(!isCompareMode);
    setComparisonIds(new Set()); // Reset selection when toggling mode
  };

  const handleSelectForComparison = (outfitId: string) => {
    setComparisonIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(outfitId)) {
        newSet.delete(outfitId);
      } else if (newSet.size < 2) {
        newSet.add(outfitId);
      }
      return newSet;
    });
  };

  const handleCompareClick = (outfitId: string) => {
    const newSet = new Set(comparisonIds);

    if (newSet.has(outfitId)) {
      newSet.delete(outfitId);
      setComparisonIds(newSet);
    } else {
      newSet.add(outfitId);
      setComparisonIds(newSet);

      // If we now have 2 items, immediately navigate
      if (newSet.size === 2) {
        const ids = Array.from(newSet);
        onNavigate('canvas', { compare1: ids[0], compare2: ids[1] });
        // Optional: Reset set after navigation so coming back starts fresh
        setComparisonIds(new Set());
      }
    }
  };

  const comparisonOutfits = useMemo(() => {
    return Array.from(comparisonIds).map(id => savedOutfits.find(o => o.id === id)).filter((o): o is SavedOutfit => o !== undefined);
  }, [comparisonIds, savedOutfits]);

  const renderContentItem = (item: SavedContent) => {
    if (item.type === 'social_media_post') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {item.sourceImageUrl && (
            <div className="md:col-span-1">
              <img src={item.sourceImageUrl} alt="Source outfit" className="w-full h-auto object-cover rounded-md" />
              <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">{t('myStylesPage_sourceOutfit')}</p>
            </div>
          )}
          <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {item.linkedinImageUrl && (
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300">{t('myStylesPage_social_linkedin')}</h4>
                <img src={item.linkedinImageUrl} alt="LinkedIn Post" className="w-full h-auto object-cover rounded-md mt-2" />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 p-2 bg-gray-100 dark:bg-gray-700/50 rounded">{item.linkedinText}</p>
              </div>
            )}
            {item.twitterImageUrl && (
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300">{t('myStylesPage_social_twitter')}</h4>
                <img src={item.twitterImageUrl} alt="Twitter/X Post" className="w-full h-auto object-cover rounded-md mt-2" />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 p-2 bg-gray-100 dark:bg-gray-700/50 rounded">{item.twitterText}</p>
              </div>
            )}
            {item.instagramImageUrl && (
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300">{t('myStylesPage_social_instagram')}</h4>
                <img src={item.instagramImageUrl} alt="Instagram Post" className="w-full h-auto object-cover rounded-md mt-2" />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 p-2 bg-gray-100 dark:bg-gray-700/50 rounded">{item.instagramText}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex gap-6">
        {item.sourceImageUrl && (item.type === 'color_analysis' || item.type === 'analysis') && (
          <div className="flex-shrink-0">
            <img src={item.sourceImageUrl} alt="Analyzed outfit" className="w-32 h-48 object-cover rounded-md shadow-md" />
          </div>
        )}
        <div className="flex-grow">
          <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: item.content }} />
        </div>
      </div>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 overflow-y-auto custom-scrollbar"
      >
        <div className="w-full max-w-7xl mx-auto mb-8">
          <div className="flex flex-col items-center mb-10">
            <BookOpenIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-3" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white text-center">
              {t('myStylesPage_title')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-2 text-center max-w-2xl">
              {t('myStylesPage_tagline')}
            </p>
          </div>

          <div className="flex justify-center border-b border-gray-300 dark:border-gray-700 mb-8">
            <button onClick={() => setActiveTab('outfits')} className={`px-6 py-3 font-semibold text-lg transition-colors ${activeTab === 'outfits' ? 'border-b-2 border-gray-900 text-gray-900 dark:border-indigo-500 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'}`}>
              {t('myStylesPage_outfitsTab')} ({savedOutfits.length})
            </button>
            <button onClick={() => setActiveTab('favorites')} className={`px-6 py-3 font-semibold text-lg transition-colors ${activeTab === 'favorites' ? 'border-b-2 border-gray-900 text-gray-900 dark:border-indigo-500 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'}`}>
              {t('myStylesPage_favoritesTab')} ({favoritedCount})
            </button>
            <button onClick={() => setActiveTab('lookbooks')} className={`px-6 py-3 font-semibold text-lg transition-colors ${activeTab === 'lookbooks' ? 'border-b-2 border-gray-900 text-gray-900 dark:border-indigo-500 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'}`}>
              {t('myStylesPage_lookbooksTab')} ({lookbooks.length})
            </button>
            <button onClick={() => setActiveTab('advice')} className={`px-6 py-3 font-semibold text-lg transition-colors ${activeTab === 'advice' ? 'border-b-2 border-gray-900 text-gray-900 dark:border-indigo-500 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'}`}>
              {t('myStylesPage_adviceTab')} ({savedContent.filter(c => c.type !== 'color_analysis' && c.type !== 'analysis').length})
            </button>
            <button onClick={() => setActiveTab('color_analysis')} className={`px-6 py-3 font-semibold text-lg transition-colors ${activeTab === 'color_analysis' ? 'border-b-2 border-gray-900 text-gray-900 dark:border-indigo-500 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'}`}>
              {t('colorStudio_title')} ({savedContent.filter(c => c.type === 'color_analysis' || c.type === 'analysis').length})
            </button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              {/* Filters */}
              {activeTab !== 'analytics' && activeTab !== 'color_analysis' && (
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <input
                    type="text"
                    placeholder={t('myStylesPage_filterPlaceholder')}
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="flex-grow p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                  />
                  {(activeTab === 'outfits' || activeTab === 'favorites') && (
                    <div className="flex gap-4">
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as any)}
                        className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm dark:text-gray-200"
                        title={t('myStylesPage_sortOrder')}
                      >
                        <option value="newest">{t('myStylesPage_sortNewest')}</option>
                        <option value="oldest">{t('myStylesPage_sortOldest')}</option>
                        <option value="name-asc">{t('myStylesPage_sortNameAsc')}</option>
                        <option value="name-desc">{t('myStylesPage_sortNameDesc')}</option>
                      </select>
                      {/* Compare Mode Toggle (Optional if using direct button, but kept for explicit mode) */}
                      <button onClick={handleToggleCompareMode} className={`p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold transition-colors ${isCompareMode ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                        {isCompareMode ? t('common_close') : t('myStylesPage_compareMode_title')}
                      </button>
                      <button onClick={() => onNavigate('style-report')} className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap">
                        {t('myStylesPage_exportBooklet')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && (
                <StyleAnalyticsDashboard
                  savedOutfits={savedOutfits}
                  lookbooks={lookbooks}
                  favoritedOutfitIds={favoritedOutfitIds}
                  userProfile={userProfile}
                />
              )}

              {activeTab === 'color_analysis' && (
                <div className="space-y-12 w-full">
                  <ColorStudioPage embedded={true} />

                  {/* Saved Color Analysis Results */}
                  <div className="space-y-6 pt-12 border-t border-gray-300 dark:border-gray-700">
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white">{t('myStylesPage_savedColorAnalysis_title')}</h2>
                      <p className="text-gray-600 dark:text-gray-400 mt-2">{t('myStylesPage_savedColorAnalysis_desc')}</p>
                    </div>

                    <div className="space-y-6">
                      {filteredAndSortedContent.length > 0 ? filteredAndSortedContent.map(item => (
                        <div key={item.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700 p-6 relative">
                          <button
                            onClick={() => onDeleteContent(item.id)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                            aria-label={`Delete ${item.title}`}
                          >
                            <Trash2Icon className="w-5 h-5" />
                          </button>
                          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg mb-2">{item.title}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{new Date(item.createdAt).toLocaleString()}</p>
                          {renderContentItem(item)}
                        </div>
                      )) : (
                        <div className="text-center py-16 text-gray-500 dark:text-gray-400 col-span-full bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                          <p>{t('myStylesPage_noSavedAnalysis')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(activeTab === 'outfits' || activeTab === 'favorites') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredAndSortedOutfits.map(outfit => {
                    const isFavorited = favoritedOutfitIds.has(outfit.id);
                    const isSelectedForCompare = comparisonIds.has(outfit.id);
                    return (
                      <div key={outfit.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700 flex flex-col ${isCompareMode ? 'cursor-pointer' : ''} ${isSelectedForCompare ? 'ring-4 ring-offset-2 ring-indigo-500' : ''}`}
                        onClick={() => isCompareMode && handleSelectForComparison(outfit.id)}>
                        <div className="relative aspect-[3/4] w-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          {outfit.previewImageUrl ? (
                            <img src={outfit.previewImageUrl} alt={outfit.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                          ) : <div className="flex items-center justify-center h-full text-gray-400 text-sm">{t('myStylesPage_noPreview')}</div>}
                          {!isCompareMode && <button
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(outfit.id); }}
                            className="absolute top-2 right-2 p-2 bg-white/50 backdrop-blur-sm rounded-full transition-colors"
                            aria-label={isFavorited ? t('myStylesPage_favorite_remove_aria') : t('myStylesPage_favorite_add_aria')}
                          >
                            <HeartIcon filled={isFavorited} className={`w-5 h-5 ${isFavorited ? 'text-red-500' : 'text-gray-700'}`} />
                          </button>}
                          {isCompareMode && (
                            <div className={`absolute inset-0 flex items-center justify-center transition-colors ${isSelectedForCompare ? 'bg-indigo-500/30' : 'bg-black/50 opacity-0 group-hover:opacity-100'}`}>
                              {isSelectedForCompare ? <CheckCircleIcon className="w-12 h-12 text-white" /> : <span className="text-white font-bold text-lg">{t('myStylesPage_compareMode_select')}</span>}
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-grow flex flex-col">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">{outfit.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {outfit.description || (outfit.layer ?
                              [
                                outfit.layer.mainGarment ? t(outfit.layer.mainGarment.nameKey) : null,
                                ...(outfit.layer.accessories || []).map(acc => t(acc.nameKey))
                              ].filter(Boolean).join(', ')
                              : t('myStylesPage_outfitDescriptionBase'))}
                          </p>
                          {outfit.tags && outfit.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {outfit.tags.map(tag => (
                                <span key={tag} className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {!isCompareMode && <div className="flex justify-between items-center mt-auto pt-2 gap-2">
                            <button
                              onClick={() => onLoadOutfit(outfit)}
                              className="flex-grow bg-gray-900 text-white font-semibold py-2 px-3 rounded-md text-sm hover:bg-gray-700 transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-500"
                              aria-label={`Load ${outfit.name}`}
                            >
                              {t('myStylesPage_loadOutfit')}
                            </button>
                            <button
                              onClick={() => handleCompareClick(outfit.id)}
                              className={`flex-shrink-0 p-2 rounded-md transition-colors ${isSelectedForCompare ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-indigo-400 dark:hover:bg-gray-700'}`}
                              title={t('myStylesPage_compareMode_title')}
                            >
                              <LayersIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => onDeleteOutfit(outfit.id)}
                              className="flex-shrink-0 text-gray-500 hover:text-red-600 p-2 rounded-md hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-500 dark:hover:bg-red-900/20"
                              aria-label={`${t('myStylesPage_deleteOutfit', { outfitName: outfit.name })}`}
                            >
                              <Trash2Icon className="w-5 h-5" />
                            </button>
                          </div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === 'lookbooks' && (
                <>
                  <div className="flex justify-end mb-6">
                    <button
                      onClick={onShowLookbookCreator}
                      disabled={userProfile.membershipLevel === 'basic'}
                      title={userProfile.membershipLevel === 'basic' ? t('feature_locked_premium_generic') : t('myStylesPage_createLookbook')}
                      className="inline-flex items-center justify-center bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-indigo-700 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      {t('myStylesPage_createLookbook')}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAndSortedLookbooks.map(lookbook => (
                      <div
                        key={lookbook.id}
                        onClick={() => lookbook.type === 'magazine' && setSelectedMagazine(lookbook)}
                        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700 ${lookbook.type === 'magazine' ? 'cursor-pointer' : ''}`}
                      >
                        <div className="aspect-video w-full bg-gray-100 dark:bg-gray-700 overflow-hidden relative">
                          <img src={lookbook.lookbookImageUrl} alt={lookbook.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                          {lookbook.type === 'magazine' && (
                            <div className="absolute top-2 right-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                              {t('magazine_badge', 'MAGAZINE')}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">{lookbook.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{t('myStylesPage_lookbookDescription', { count: lookbook.outfitIds.length })}</p>
                        </div>
                      </div>
                    ))}
                    {filteredAndSortedLookbooks.length === 0 && (
                      <div className="text-center py-16 text-gray-500 dark:text-gray-400 col-span-full">
                        <p>{t('myStylesPage_noLookbooks')}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'advice' && (
                <div className="space-y-6">
                  {filteredAndSortedContent.length > 0 ? filteredAndSortedContent.map(item => (
                    <div key={item.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700 p-6 relative">
                      <button
                        onClick={() => onDeleteContent(item.id)}
                        className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label={`Delete ${item.title}`}
                      >
                        <Trash2Icon className="w-5 h-5" />
                      </button>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg mb-2">{item.title}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{new Date(item.createdAt).toLocaleString()}</p>
                      {renderContentItem(item)}
                    </div>
                  )) : (
                    <div className="text-center py-16 text-gray-500 dark:text-gray-400 col-span-full">
                      <p>{t('myStylesPage_noAdvice')}</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        <Footer onNavigate={onNavigate} />
      </motion.div >

      <AnimatePresence>
        {(isCompareMode || comparisonIds.size > 0) && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md p-4 border-t border-gray-200 dark:border-gray-700 shadow-lg z-30"
          >
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center gap-4">
                <p className="font-semibold text-lg dark:text-white">{t('myStylesPage_compareMode_title')}</p>
                <div className="flex items-center gap-3">
                  {comparisonOutfits.map(outfit => (
                    <img key={outfit.id} src={outfit.previewImageUrl || ''} alt={outfit.name} className="w-12 h-16 object-cover rounded-md border-2 border-white dark:border-gray-600 shadow-sm" />
                  ))}
                </div>
              </div>
              <button
                onClick={() => {
                  if (comparisonIds.size === 2) {
                    const ids = Array.from(comparisonIds);
                    // Navigation instead of modal as requested
                    onNavigate('canvas', { compare1: ids[0], compare2: ids[1] });
                    // Reset comparison after navigation
                    setComparisonIds(new Set());
                  }
                }}
                disabled={comparisonIds.size !== 2}
                className="bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {t('myStylesPage_compareMode_compareButton', { count: comparisonIds.size })}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedMagazine && (
        <MagazineLookbook
          lookbook={selectedMagazine}
          savedOutfits={savedOutfits}
          onClose={() => setSelectedMagazine(null)}
        />
      )}
    </>
  );
};

export default MyStylesPage;
